import {makeAutoObservable, observable} from 'mobx'
import {ChangeEvent} from 'react'
import {Lang} from 'yougile-lang'
import {logger, saveAsXlsx, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  TimetrackerBoardSettings,
  TimetrackerValue,
  User
} from 'yougile-model'
import {Sync} from '@client/sync'
import {DateRange} from '@components/common/date-picker/date-picker'
import {getUserAccountName} from '@components/user-name'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {Project} from '@extensions/project'
import {Task} from '@extensions/task'
import {
  getBoardName,
  getDateRange,
  getTimeTrackingSticker,
  getUser,
  listBoards
} from './utils'

interface State {
  companyPage: boolean
  filter: {
    board: Hub
    datesType: DatesType
    dates: [Date, Date]
    chooseDate: boolean
  }
  table: {
    tasks: TableItem[]
    include: 'open' | 'closed' | 'all'
    sort: {
      field: SortField
      asc: boolean
    }
    chartData: any
    chartHours: boolean
    openTot: string
    closedTot: number
  }
  total: {
    plan: string
    work: string
    count: number
  }
}

export class StatsPageStore {
  state: State

  constructor(public board: Hub) {
    this.state = {
      companyPage: false,
      table: {
        tasks: [] as TableItem[],
        include: 'open',
        sort: {
          field: 'user',
          asc: false
        },
        chartData: undefined,
        chartHours: false,
        openTot: '',
        closedTot: 0
      },
      total: {
        plan: '',
        work: '',
        count: 0
      },
      filter: {
        board,
        datesType: '2week',
        dates: [
          Time.toLocalDate(Time.now() - 7 * hour24),
          Time.toLocalDate(Time.now())
        ],
        chooseDate: false
      }
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        board: false
      },
      {autoBind: true}
    )

    this.syncOnUpdate()
  }

  syncOnUpdate(): void {
    this.update()
  }

  update(): void {
    const dates = this.getDates()
    const begin = new Date(dates[0])
    // start of day
    begin.setHours(0)
    begin.setMinutes(0)

    const end = new Date(dates[1])
    // end of day
    end.setHours(0)
    end.setMinutes(0)
    end.setTime(end.getTime() + 24 * 3600 * 1000)

    const days = Math.round((end.getTime() - begin.getTime()) / hour24)
    const openByDay: number[] = []
    const closedByDay: number[] = []

    const filter = {
      dates: [begin, end],
      include: this.state.table.include
    }

    let totPlan = 0
    let totWork = 0
    let count = 0
    let res: TableItem[] = []

    if (!this.state.filter.board) {
      this.state.filter.board = this.getBoards()[0]
      if (!this.state.filter.board) {
        this.state.filter = {...this.state.filter}
        return
      }
    }
    const columns = Board.listColumns(this.state.filter.board, {
      noMirrors: true
    })

    const ttSticker = getTimeTrackingSticker()

    columns.forEach(g =>
      Column.listTasks(g, false).forEach(t => {
        const conf = this.state.filter.board.stickers[
          ttSticker.id
        ] as TimetrackerBoardSettings
        const open =
          !Task.isArchived(t) && !(conf?.complete && g.id in conf.complete)
        const closeTs = open ? undefined : t.complete || t.timestamp

        if (
          filter.dates &&
          closeTs &&
          (closeTs < filter.dates[0].getTime() ||
            closeTs > filter.dates[1].getTime())
        ) {
          return
        }

        const val = t.stickers[ttSticker.id] as TimetrackerValue
        res.push({
          id: t.id,
          name: Util.limitStr(t.title, 200),
          user: getUser(t),
          plan: val ? val.plan + 'h' : '–',
          work: val ? val.work + 'h' : '–',
          created: Time.formatDate(t.timestamp),
          closed: closeTs ? Time.formatDate(closeTs) : '—',
          open,
          rank: val ? val.plan : -1,
          planN: val ? val.plan : -1,
          workN: val ? val.work : -1,
          timestamp: t.timestamp,
          closeTs
        })

        const amount = this.state.table.chartHours ? val?.plan || 0 : 1
        if (t.timestamp < begin.getTime()) {
          openByDay[0] = (openByDay[0] || 0) + amount
        } else {
          const sh = Math.ceil((t.timestamp - begin.getTime()) / hour24)
          openByDay[sh] = (openByDay[sh] || 0) + amount
        }

        if (!open && closeTs) {
          if (closeTs > begin.getTime() && closeTs < end.getTime()) {
            const sh = Math.ceil((closeTs - begin.getTime()) / hour24)
            closedByDay[sh] = (closedByDay[sh] || 0) + amount
          }
        }

        if (
          (filter.include === 'open' && open) ||
          (filter.include === 'closed' && !open) ||
          filter.include === 'all'
        ) {
          count++
          if (val) {
            totPlan += val.plan
            totWork += val.work
          }
        }
      })
    )

    if (filter.include === 'open') {
      res = res.filter(t => t.open)
    } else if (filter.include === 'closed') {
      res = res.filter(t => !t.open)
    }

    let openAcc = 0
    let closedAcc = 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = []
    for (let i = 0; i < days + 1; i++) {
      openAcc += openByDay[i] || 0
      closedAcc += closedByDay[i] || 0
      const name = Time.formatDate(begin.getTime() + (i - 1) * hour24)
      data.push({
        name,
        [Lang.msg.tsKeyOpen]: openAcc - closedAcc,
        [Lang.msg.tsKeyClosed]: closedAcc
      })
    }

    this.state.table.openTot = +(openAcc - closedAcc).toFixed(1) + ''
    this.state.table.closedTot = closedAcc
    this.state.table.chartData = data

    this.state.total = {
      plan: totPlan + 'h',
      work: totWork + 'h',
      count
    }
    let sortFunc: (a: TableItem, b: TableItem) => number
    const {asc, field} = this.state.table.sort
    switch (field) {
      case 'text':
        sortFunc = (a, b) => a.name.localeCompare(b.name) * (asc ? 1 : -1)
        break
      case 'created':
        sortFunc = (a, b) => (a.timestamp - b.timestamp) * (asc ? 1 : -1)
        break
      case 'closed':
        sortFunc = (a, b) =>
          (+!!a.closeTs - +!!b.closeTs ||
            a.closeTs - b.closeTs ||
            a.timestamp - b.timestamp) * (asc ? 1 : -1)
        break
      case 'plan':
        sortFunc = (a, b) => (a.planN - b.planN) * (asc ? 1 : -1)
        break
      case 'work':
        sortFunc = (a, b) => (a.workN - b.workN) * (asc ? 1 : -1)
        break
      case 'user':
        sortFunc = (a, b) =>
          (+!!a.user - +!!b.user ||
            -(a.user ? a.user.timestamp : 0) +
              (b.user ? b.user.timestamp : 0) ||
            (a.closeTs || a.timestamp) - (b.closeTs || b.timestamp)) *
          (asc ? 1 : -1)
        break
      default:
        logger.warn('Unrecognized sort field', field)
    }
    this.state.table.tasks = res.sort(sortFunc)

    this.state.table = {...this.state.table}
    this.state.filter = {...this.state.filter}
  }

  getBoards(): Hub[] {
    const box = Sync.getCurrentSandbox()
    const res: Hub[] = []
    Project.listProjects(box.companyId, 'not-archive').forEach(p =>
      Project.listBoards(p, true)
        .filter(b => getTimeTrackingSticker().id in b.stickers)
        .forEach(b => res.push(b))
    )
    return res
  }

  getDates(): [number, number] | undefined {
    const type = this.state.filter.datesType

    switch (type) {
      case 'custom':
        return [
          this.state.filter.dates[0].getTime(),
          this.state.filter.dates[1].getTime()
        ]
      case 'week':
        return getDateRange(7, 0)
      case '2week':
        return getDateRange(14, 0)
      case 'month':
        return getDateRange(30, 0)
      default:
        return undefined
    }
  }

  downloadXLSX(): void {
    const box = Sync.getCurrentSandbox()
    const dates = this.getDates().map(ts => Time.formatDate(ts))
    const data: Array<Array<string | number>> = [
      ['Dates', dates[0], dates[1]],
      [],
      [
        'Task',
        'Completed',
        'User',
        'Estimate, h',
        'Work, h',
        'Closed',
        'Created'
      ]
    ]
    this.state.table.tasks.forEach(t =>
      data.push([
        t.name,
        t.open ? 0 : 1,
        getUserAccountName(t.user),
        t.planN > -1 ? t.planN : '',
        t.workN > -1 ? t.workN : '',
        t.open ? '' : Time.toStandardString(t.closeTs),
        Time.toStandardString(t.timestamp)
      ])
    )
    const companyName = (
      box.get(box.companyId).title +
      '-' +
      getBoardName(this.state.filter.board)
        .replace(/\//g, '-')
        .replace(' - ', '-') +
      '-' +
      dates.join('-')
    ).replace(/ /g, '-')
    saveAsXlsx(companyName + '.xlsx', data)
  }

  tableSetInclude(val: 'open' | 'closed' | 'all'): void {
    this.state.table.include = val
    this.update()
  }

  updateChartHours(state: boolean): void {
    this.state.table.chartHours = state
    this.update()
  }

  updateFilterBoard(evt: ChangeEvent<HTMLSelectElement>): void {
    this.state.filter.board = listBoards().find(b => b.id === evt.target.value)
    this.update()
  }

  updateFilterDatesType(evt: ChangeEvent<HTMLSelectElement>): void {
    this.state.filter.datesType = evt.target.value as DatesType
    this.update()
  }

  sortChange(field: SortField): void {
    const {sort} = this.state.table
    if (sort.field === field) {
      sort.asc = !sort.asc
    } else {
      sort.field = field
      sort.asc = false
    }
    this.update()
  }

  get datesTxt(): string {
    if (!this.state.filter.dates) {
      return 'any date'
    }

    const [d1, d2] = this.state.filter.dates
    return (
      Time.formatDate(Time.toTimestamp(d1)) +
      ' - ' +
      Time.formatDate(Time.toTimestamp(d2))
    )
  }

  setChooseDate(value: boolean): void {
    this.state.filter.chooseDate = value
    this.update()
  }

  onDateChoose(range: DateRange): void {
    this.state.filter.dates = [range.start, range.end]
    this.update()
  }
}

export const [StatsPageContext, useStatsPageStore] =
  createUseStore(StatsPageStore)

interface TableItem {
  id: DataObjectId
  name: string
  user: User
  plan: string
  work: string
  created: string
  closed: string
  open: boolean
  rank: number
  planN: number
  workN: number
  timestamp: number
  closeTs: number
}

const hour24 = 24 * 3600 * 1000

type SortField = 'text' | 'plan' | 'work' | 'created' | 'closed' | 'user'
type DatesType = 'week' | '2week' | 'month' | 'custom'
